import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';

import { setVideoUploadSuccess } from '@assetManagement/store/uploadVideo';
import { ASSET_UPLOAD_URL } from '@assetManagement/data/assetUpload';
import ASLVideoUploadWaitModal from '../ASLVideoUploadWaitModal';
import FieldErrors from 'components/FieldErrors';
import Table from 'components/Table';
import TableBody from 'components/TableBody';
import TableCell from 'components/TableCell';
import TableHead from 'components/TableHead';
import TableRow from 'components/TableRow';
import UploadToS3 from '@assetManagement/utils/uploadToS3';

import styles from './ASLVideoUploadTable.module.scss';

export default function ASLVideoUploadTable({
  videoFiles,
  zipFile,
  hasError,
  isUploading,
  setIsUploading,
  resetZipFile,
}) {
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState(0);

  const handleVideoUpload = useCallback(async () => {
    if (isUploading) return;

    const uploadToS3 = new UploadToS3();

    const uploadResults = {
      successful: [],
      failed: [],
    };
    setUploadedFiles(0);

    try {
      setIsUploading(true);
      for (const videoFile of videoFiles || []) {
        try {
          const uploadResponse = await uploadToS3.uploadAttachment(
            ASSET_UPLOAD_URL,
            videoFile,
            { video_duration: videoFile.duration }
          );

          if (uploadResponse?.success) {
            uploadResults.successful.push(videoFile?.name);
          } else {
            uploadResults.failed.push({
              fileName: videoFile?.name,
              reason: uploadResponse?.message || 'Unknown upload error',
            });
          }
        } catch (fileUploadError) {
          uploadResults.failed.push({
            fileName: videoFile?.name,
            reason: fileUploadError?.message || 'Upload failed',
          });
        }
        setUploadedFiles((prev) => prev + 1);
      }

      if (uploadResults?.successful?.length === videoFiles?.length) {
        setTimeout(() => {
          resetZipFile();
        }, 2000);
        cogoToast.success('All files uploaded successfully');
      } else if (uploadResults?.failed?.length > 0) {
        cogoToast.error(
          'Some files failed to upload, please check the history tab'
        );
      } else {
        cogoToast.error('All uploads failed');
      }
      setIsUploading(false);
      dispatch(setVideoUploadSuccess(true));
    } catch (uploadError) {
      setIsUploading(false);
      dispatch(setVideoUploadSuccess(true));
    }
  }, [dispatch, isUploading, resetZipFile, setIsUploading, videoFiles]);

  return (
    <div className="p-b-20">
      <h3>Extracted Video Files:</h3>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {videoFiles.map((video, index) => (
            <TableRow
              key={`video-${index}`}
              className={styles.root}
            >
              <TableCell>{video.name}</TableCell>
              <TableCell>{video.duration} s</TableCell>
              <TableCell>
                {video.error?.uploadVideo ? (
                  <FieldErrors
                    name="uploadVideo"
                    errors={video.error}
                    prefixName={false}
                  />
                ) : (
                  <>-</>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="m-t-20">
        <Tappable
          className="btn btn-primary"
          onClick={handleVideoUpload}
          disabled={!zipFile || hasError || isUploading}
        >
          {isUploading ? 'Uploading...' : 'Upload Files to S3'}
        </Tappable>
      </div>
      {isUploading && (
        <ASLVideoUploadWaitModal
          uploadedFiles={uploadedFiles}
          totalFiles={videoFiles?.length}
        />
      )}
    </div>
  );
}
